import React, { useLayoutEffect, useRef } from "react";
import {
  Clock,
  DollarSign,
  ClipboardCheck,
  CheckCircle2,
  BarChart3,
  PieChart,
  Building2,
  Briefcase,
  Users,
  Search,
  FileSignature,
  Layout,
  Send,
  FileText
} from "lucide-react";
import Navbar from "../../Components/Navbar";
import ChatbotWidget from "../ChatComponent";
import Call from "../../Components/Call";
import Footer from "../../Components/Footer";
import "../../Styling/Landing_Pages_css/Proposal.css";
import HeroLandingPage from "../../Components/LandingPagesComponents/Hero_Landing_Page";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
  initial: { opacity: 0 },
  whileInView: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
  viewport: { once: true },
};

export default function ProposalLandingPage() {
  const canvasRef = useRef(null);

  useLayoutEffect(() => {
    // Connected Nodes Animation
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width, height, nodes = [];

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", resize);
    resize();

    class Node {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = 3; // Bolder nodes
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(91, 76, 255, 0.8)"; // Dense color
        ctx.fill();
      }
    }

    for (let i = 0; i < 50; i++) nodes.push(new Node());

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      nodes.forEach((node, i) => {
        node.update();
        node.draw();

        for (let j = i + 1; j < nodes.length; j++) {
          const dx = node.x - nodes[j].x;
          const dy = node.y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            // Prominent connections
            ctx.strokeStyle = `rgba(0, 191, 255, ${(1 - dist / 150) * 0.8})`;
            ctx.lineWidth = 0.8;
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      });
      requestAnimationFrame(animate);
    };

    animate();

    // Scroll Animations
    const sections = gsap.utils.toArray('section');
    sections.forEach(section => {
      gsap.fromTo(section,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "expo.out",
          scrollTrigger: {
            trigger: section,
            start: "top 85%"
          }
        }
      );
    });

    return () => window.removeEventListener("resize", resize);
  }, []);
  const slides = [
    {
      image: "/images/Landing_page_images/proposal/proposal_1.png",
      heading: "Faster Proposals. Cleaner Deals.",
      subheading:
        "Stop spending days assembling proposals. Generate consistent, client-ready proposals with structured content, pricing presentation, and approval flows.",
    },
    {
      image: "/images/Landing_page_images/proposal/proposal_2.png",
      heading: "Proposal Bottlenecks Cost Deals",
      subheading:
        "Standardize how proposals are created, reviewed, and delivered so your team moves faster without sacrificing quality or control.",
    },
    {
      image: "/images/Landing_page_images/proposal/proposal_3.png",
      heading: "A Proposal System Built for Sales Teams",
      subheading:
        "This system is configured, branded, and licensed around your sales process, deal size, and approval requirements.",
    },
  ];

  return (
    <>
      <Navbar></Navbar>

      {/* GSAP Animated Background */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: -1,
          pointerEvents: 'none',
          opacity: 0.7 // Higher prominence
        }}
      />
      <HeroLandingPage
        slides={slides}
        badgeText="AI-Powered Proposal Generation System"
        primaryBtn={{
          text: "Request Demo Access →",
          link: "https://studio.digitaldreamworksagency.com/access",
        }}

      />

      <section className="ProposalGen-problem-section">
        <div className="container">
          <motion.h2
            className="ProposalGen-problem-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Proposal Creation Slows Revenue
          </motion.h2>
          <motion.p
            className="ProposalGen-problem-desc"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Most agencies and service businesses have deals delayed because
            proposals take too long to create, are inconsistent, and lack the
            speed decision-makers expect.
          </motion.p>

          <motion.div
            className="ProposalGen-problem-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="ProposalGen-problem-card" variants={fadeInUp}>
              <div className="ProposalGen-problem-icon-wrapper">
                <Clock className="ProposalGen-problem-icon-svg" />
              </div>
              <p className="ProposalGen-problem-text">
                Proposals take days to create from scratch
              </p>
            </motion.div>
            <motion.div className="ProposalGen-problem-card" variants={fadeInUp}>
              <div className="ProposalGen-problem-icon-wrapper">
                <DollarSign className="ProposalGen-problem-icon-svg" />
              </div>
              <p className="ProposalGen-problem-text">
                Inconsistent pricing and scoping across deals
              </p>
            </motion.div>

            <motion.div className="ProposalGen-problem-card" variants={fadeInUp}>
              <div className="ProposalGen-problem-icon-wrapper">
                <BarChart3 className="ProposalGen-problem-icon-svg" />
              </div>
              <p className="ProposalGen-problem-text">
                No visibility into proposal performance or win rates
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="ProposalGen-process-section">
        <div className="container">
          <motion.h2
            className="ProposalGen-process-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            From Opportunity to Signed Deal
          </motion.h2>
          <motion.p
            className="ProposalGen-process-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Automated workflows that spin proposals out the door in minutes, not
            days
          </motion.p>

          <motion.div
            className="ProposalGen-process-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="ProposalGen-process-card" variants={fadeInUp}>
              <div className="ProposalGen-process-icon">
                <Search size={24} />
              </div>
              <h3 className="ProposalGen-process-title-card">Intake</h3>
              <p className="ProposalGen-process-desc">
                Enter deal details and client scope
              </p>
            </motion.div>
            <motion.div className="ProposalGen-process-card" variants={fadeInUp}>
              <div className="ProposalGen-process-icon">
                <Layout size={24} />
              </div>
              <h3 className="ProposalGen-process-title-card">Generation</h3>
              <p className="ProposalGen-process-desc">
                System generates proposal templates
              </p>
            </motion.div>
            <motion.div className="ProposalGen-process-card" variants={fadeInUp}>
              <div className="ProposalGen-process-icon">
                <ClipboardCheck size={24} />
              </div>
              <h3 className="ProposalGen-process-title-card">Review</h3>
              <p className="ProposalGen-process-desc">
                Finalize and get approvals instantly
              </p>
            </motion.div>
            <motion.div className="ProposalGen-process-card" variants={fadeInUp}>
              <div className="ProposalGen-process-icon">
                <Send size={24} />
              </div>
              <h3 className="ProposalGen-process-title-card">Delivery</h3>
              <p className="ProposalGen-process-desc">
                Send to client and track signature
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="ProposalGen-automates-section">
        <div className="container">
          <motion.h2
            className="ProposalGen-automates-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What the System Automates
          </motion.h2>

          <motion.div
            className="ProposalGen-automates-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="ProposalGen-automates-card" variants={fadeInUp}>
              <div className="ProposalGen-automates-icon">
                <FileText size={28} />
              </div>
              <h3 className="ProposalGen-automates-card-title">
                Proposal generation from templates and data
              </h3>
              <p className="ProposalGen-automates-desc">
                Create personalized proposals in seconds
              </p>
            </motion.div>
            <motion.div className="ProposalGen-automates-card" variants={fadeInUp}>
              <div className="ProposalGen-automates-icon">
                <DollarSign size={28} />
              </div>
              <h3 className="ProposalGen-automates-card-title">
                Dynamic pricing based on scope and parameters
              </h3>
              <p className="ProposalGen-automates-desc">
                Consistent, rule-based pricing calculations
              </p>
            </motion.div>
            <motion.div className="ProposalGen-automates-card" variants={fadeInUp}>
              <div className="ProposalGen-automates-icon">
                <PieChart size={28} />
              </div>
              <h3 className="ProposalGen-automates-card-title">
                Client data auto-population
              </h3>
              <p className="ProposalGen-automates-desc">
                Reduce manual data entry and errors
              </p>
            </motion.div>
            <motion.div className="ProposalGen-automates-card" variants={fadeInUp}>
              <div className="ProposalGen-automates-icon">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="ProposalGen-automates-card-title">
                Approval workflows and versioning
              </h3>
              <p className="ProposalGen-automates-desc">
                Track changes and get sign-offs instantly
              </p>
            </motion.div>
            <motion.div className="ProposalGen-automates-card" variants={fadeInUp}>
              <div className="ProposalGen-automates-icon">
                <FileSignature size={28} />
              </div>
              <h3 className="ProposalGen-automates-card-title">
                Built-format export (PDF, DOCX, HTML)
              </h3>
              <p className="ProposalGen-automates-desc">
                Deliver in any format your clients need
              </p>
            </motion.div>
            <motion.div className="ProposalGen-automates-card" variants={fadeInUp}>
              <div className="ProposalGen-automates-icon">
                <BarChart3 size={28} />
              </div>
              <h3 className="ProposalGen-automates-card-title">
                Performance tracking and analytics
              </h3>
              <p className="ProposalGen-automates-desc">
                See which proposals win and why
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="ProposalGen-difference-section">
        <div className="container">
          <motion.h2
            className="ProposalGen-difference-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            The Difference
          </motion.h2>

          <motion.div
            className="ProposalGen-difference-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div
              className="ProposalGen-difference-card ProposalGen-difference-card-old"
              variants={fadeInUp}
            >
              <div className="ProposalGen-difference-header">
                <span className="ProposalGen-difference-header-icon">✕</span>
                <span>3-5 days to create</span>
              </div>
              <div className="ProposalGen-difference-item">
                Manual template editing
              </div>
              <div className="ProposalGen-difference-item">
                Inconsistent pricing
              </div>
              <div className="ProposalGen-difference-item">Email approvals</div>
              <div className="ProposalGen-difference-item">
                Lost context and audits
              </div>
            </motion.div>

            <motion.div
              className="ProposalGen-difference-card ProposalGen-difference-card-new"
              variants={fadeInUp}
            >
              <div className="ProposalGen-difference-header">
                <span className="ProposalGen-difference-header-icon">✓</span>
                <span>15 minutes to generate</span>
              </div>
              <div className="ProposalGen-difference-item">
                Automated proposal generation
              </div>
              <div className="ProposalGen-difference-item">
                Dynamic pricing rules
              </div>
              <div className="ProposalGen-difference-item">Workflow approvals</div>
              <div className="ProposalGen-difference-item">
                Full audit trail and analytics
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="ProposalGen-teams-section">
        <div className="container">
          <motion.h2
            className="ProposalGen-teams-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            How Teams Use It
          </motion.h2>

          <motion.div
            className="ProposalGen-teams-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="ProposalGen-teams-card" variants={fadeInUp}>
              <div className="ProposalGen-teams-icon">
                <Building2 size={24} />
              </div>
              <h3 className="ProposalGen-teams-card-title">Agencies</h3>
              <p className="ProposalGen-teams-card-desc">
                Speed up proposal cycles for faster revenue closure without
                creating better teams.
              </p>
            </motion.div>
            <motion.div className="ProposalGen-teams-card" variants={fadeInUp}>
              <div className="ProposalGen-teams-icon">
                <Briefcase size={24} />
              </div>
              <h3 className="ProposalGen-teams-card-title">Service Businesses</h3>
              <p className="ProposalGen-teams-card-desc">
                Standardize proposals and pricing across teams. Measurable for
                custom scopes.
              </p>
            </motion.div>
            <motion.div className="ProposalGen-teams-card" variants={fadeInUp}>
              <div className="ProposalGen-teams-icon">
                <Users size={24} />
              </div>
              <h3 className="ProposalGen-teams-card-title">Sales Teams</h3>
              <p className="ProposalGen-teams-card-desc">
                Generate proposals in minutes, not days. Close faster and focus
                on selling.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <div className="LD-divider"></div>
      <ChatbotWidget></ChatbotWidget>
      <Call></Call>
      <Footer></Footer>
    </>
  );
}
